// TODO: Detailed review and refactoring of this controller is pending.
import ApplicationController from "./application_controller";

export default class extends ApplicationController {
  static targets = ["chart", "tableBody", "selectedName", "tableTitle", "listItem"];

  connect() {
    this.loadStatisticsFromDataset();
    this.loadSettingsFromDataset();
    // After loading statistics and settings, initialize the initial chart.
    this.initializeInitialChart();
  }

  // Read JSON blobs for all users / institutions statistics from data attributes.
  // Values are generated server‑side and may be large, so we only parse once.
  loadStatisticsFromDataset() {
    try {
      const usersStatisticsJson = this.element.dataset.allUsersStatistics || "{}";
      this.allUsersStatistics = JSON.parse(usersStatisticsJson);
    } catch (error) {
      this.allUsersStatistics = {};
    }

    try {
      const institutionsStatisticsJson = this.element.dataset.allInstitutionsStatistics || "{}";
      this.allInstitutionsStatistics = JSON.parse(institutionsStatisticsJson);
    } catch (error) {
      this.allInstitutionsStatistics = {};
    }
  }

  // Read settings (view type, chart type, selection, date range etc.)
  // from Stimulus values rendered on the root element.
  loadSettingsFromDataset() {
    const dataset = this.element.dataset;

    this.viewType = dataset.lexiqamusIndividualStatisticsViewTypeValue || "users";
    this.chartType = dataset.lexiqamusIndividualStatisticsChartTypeValue || "line";
    this.sortOrder = dataset.lexiqamusIndividualStatisticsSortOrderValue || "desc";
    this.selectedId = parseInt(dataset.lexiqamusIndividualStatisticsSelectedIdValue || "0", 10) || 0;
    this.startDate = dataset.lexiqamusIndividualStatisticsStartDateValue;
    this.endDate = dataset.lexiqamusIndividualStatisticsEndDateValue;
    this.aggregation = dataset.lexiqamusIndividualStatisticsAggregationValue || "date";
  }

  // Initial chart bootstrap based on current view type and available data
  initializeInitialChart() {
    // Ensure Chart.js and chart target are available before proceeding
    if (typeof Chart === "undefined" || !this.hasChartTarget) return;

    if (this.viewType === "users") {
      const usersKeys = Object.keys(this.allUsersStatistics || {});
      if (usersKeys.length > 0) {
        this.initializeUserChart();
      }
    } else if (this.viewType === "institutions") {
      const institutionsKeys = Object.keys(this.allInstitutionsStatistics || {});
      if (institutionsKeys.length > 0) {
        this.initializeInstitutionChart();
      }
    }
  }

  // Render the statistics table under the chart from a chart data payload.
  // Expects the pattern `@lq_*_statistics` JSON:
  // { labels: [], search_count: [], click_count: [] }
  renderTableFromChartData(chartData) {
    if (!this.hasTableBodyTarget || !chartData) return;

    this.tableBodyTarget.innerHTML = "";

    // Get chart data arrays (oldest to newest order)
    const labelsOldestToNewest       = chartData.labels || [];
    const searchCountsOldestToNewest = chartData.search_count || [];
    const clickCountsOldestToNewest  = chartData.click_count || [];

    // Calculate cumulative values in chronological order (oldest to newest) first
    let cumulativeSearch = 0;
    let cumulativeClick = 0;
    const cumulativeSearchArray = [];
    const cumulativeClickArray = [];
    const cumulativeTotalArray = [];

    labelsOldestToNewest.forEach((_label, index) => {
      const searchCount = parseInt(searchCountsOldestToNewest[index] || 0, 10);
      const clickCount = parseInt(clickCountsOldestToNewest[index] || 0, 10);

      cumulativeSearch += searchCount;
      cumulativeClick += clickCount;
      const cumulativeTotal = cumulativeSearch + cumulativeClick;

      cumulativeSearchArray.push(cumulativeSearch);
      cumulativeClickArray.push(cumulativeClick);
      cumulativeTotalArray.push(cumulativeTotal);
    });

    // Reverse arrays for table display (newest to oldest) while keeping chart data in chronological order
    // Use .slice() to create a copy first, then .reverse() - this prevents mutating the original arrays used by charts
    const labelsNewestToOldest           = labelsOldestToNewest.slice().reverse();
    const searchCountsNewestToOldest     = searchCountsOldestToNewest.slice().reverse();
    const clickCountsNewestToOldest      = clickCountsOldestToNewest.slice().reverse();
    const cumulativeSearchNewestToOldest = cumulativeSearchArray.slice().reverse();
    const cumulativeClickNewestToOldest  = cumulativeClickArray.slice().reverse();
    const cumulativeTotalNewestToOldest  = cumulativeTotalArray.slice().reverse();

    // Use pre-calculated cumulative values
    labelsNewestToOldest.forEach((label, index) => {
      const searchCount      = searchCountsNewestToOldest[index] || 0;
      const clickCount       = clickCountsNewestToOldest[index] || 0;
      const cumulativeSearch = cumulativeSearchNewestToOldest[index] || 0;
      const cumulativeClick  = cumulativeClickNewestToOldest[index] || 0;
      const cumulativeTotal  = cumulativeTotalNewestToOldest[index] || 0;

      const row = document.createElement("tr");
      row.innerHTML = `
        <td>${label}</td>
        <td>${searchCount}</td>
        <td>${cumulativeSearch}</td>
        <td>${clickCount}</td>
        <td>${cumulativeClick}</td>
        <td>${cumulativeTotal}</td>
      `;
      this.tableBodyTarget.appendChild(row);
    });
  }

  chartDataForId(id) {
    // String version of the selected user/institution id, used as hash key
    const statisticsIdKey = id.toString();

    // Return chart data for users
    if (this.viewType === "users" && this.allUsersStatistics) {
      const stats = this.allUsersStatistics || {};
      return stats[statisticsIdKey] || null;
    }

    if (this.viewType === "institutions" && this.allInstitutionsStatistics) {
      const stats = this.allInstitutionsStatistics || {};
      return stats[statisticsIdKey] || null;
    }

    return null;
  }

  // User clicked on a user row in the left list.
  selectUser(event) {
    const userId = parseInt(event.currentTarget.dataset.userId, 10);
    const userName = event.currentTarget.dataset.userName;

    this.handleSelectionChange({
      id: userId,
      name: userName,
      chartType: "user",
      urlParamKey: "selected_id"
    });
  }

  // User clicked on an institution row in the left list.
  selectInstitution(event) {
    const institutionId = parseInt(event.currentTarget.dataset.institutionId, 10);
    const institutionName = event.currentTarget.dataset.institutionName;

    this.handleSelectionChange({
      id: institutionId,
      name: institutionName,
      chartType: "institution",
      urlParamKey: "selected_id"
    });
  }

  // Shared selection handler for users/institutions.
  handleSelectionChange({ id, name, chartType, urlParamKey }) {
    const chartData = this.chartDataForId(id);

    // If we don't have preloaded data for this id, fallback to server‑side reload.
    if (!chartData) {
      this.reloadWithParams({ [urlParamKey]: id });
      return;
    }

    const chartInstance = chartType === "user" ? this.userChart : this.institutionChart;
    if (!chartInstance) return;

    this.updateChartSeries(chartType, chartData);
    this.renderTableFromChartData(chartData);
    this.updateSelectionUiState(name, event);
    this.updateSelectedIdInUrl(id);
  }

  // Toggle between line and bar chart types for current chart.
  toggleChartType(event) {
    const chartType = event.currentTarget?.value || event.target?.value || "line";
    const chart = this.userChart || this.institutionChart;
    if (!chart) return;

    chart.config.type = chartType;

    chart.data.datasets.forEach((dataset, index) => {
      if (chartType === "bar") {
        if (index === 0) dataset.backgroundColor = "#0162CC";
        else if (index === 1) dataset.backgroundColor = "#FFA601";
        else if (index === 2) dataset.backgroundColor = "#008000";
        dataset.fill = false;
      } else {
        if (index === 0) dataset.backgroundColor = "rgba(1, 98, 204, 0.2)";
        else if (index === 1) dataset.backgroundColor = "rgba(255, 166, 1, 0.2)";
        else if (index === 2) dataset.backgroundColor = "rgba(0, 128, 0, 0.2)";
        dataset.fill = true;
      }
    });

    chart.update({
      duration: 300,
      easing: "easeInOutQuart"
    });

    const url = new URL(window.location.href);
    url.searchParams.set("chart_type", chartType);
    window.history.replaceState({}, "", url.toString());
  }

  initializeUserChart() {
    if (!this.hasChartTarget || typeof Chart === "undefined") return;

    if (this.userChart) {
      this.userChart.destroy();
    }

    const initialUserId = this.selectedId && this.selectedId !== 0 ? this.selectedId : this.firstUserId();
    const initialData = this.chartDataForId(initialUserId) || { labels: [], search_count: [], click_count: [], total_count: [] };
    const chartType = this.chartType;

    this.userChart = new Chart(this.chartTarget, {
      type: chartType,
      data: this.buildChartConfig(initialData, chartType, "User Statistics Over Time"),
      options: this.defaultChartOptions("User Statistics Over Time")
    });

    this.renderTableFromChartData(initialData);
    this.updateExportLinks(initialUserId);
  }

  initializeInstitutionChart() {
    if (!this.hasChartTarget || typeof Chart === "undefined") return;

    if (this.institutionChart) {
      this.institutionChart.destroy();
    }

    const initialInstitutionId = this.selectedId && this.selectedId !== 0 ? this.selectedId : this.firstInstitutionId();
    const initialData = this.chartDataForId(initialInstitutionId) || { labels: [], search_count: [], click_count: [], total_count: [] };
    const chartType = this.chartType;

    this.institutionChart = new Chart(this.chartTarget, {
      type: chartType,
      data: this.buildChartConfig(initialData, chartType, "Institution Statistics Over Time"),
      options: this.defaultChartOptions("Institution Statistics Over Time")
    });

    this.renderTableFromChartData(initialData);
    this.updateExportLinks(initialInstitutionId);
  }

  buildChartConfig(chartData, chartType, _title) {
    const labels = chartData.labels || [];

    return {
      labels: labels,
      datasets: [
        {
          label: "Search Count",
          data: chartData.search_count || [],
          backgroundColor: chartType === "bar" ? "#0162CC" : "rgba(1, 98, 204, 0.2)",
          borderColor: "#0162CC",
          borderWidth: 2,
          fill: chartType === "line"
        },
        {
          label: "Click Count",
          data: chartData.click_count || [],
          backgroundColor: chartType === "bar" ? "#FFA601" : "rgba(255, 166, 1, 0.2)",
          borderColor: "#FFA601",
          borderWidth: 2,
          fill: chartType === "line"
        },
        {
          label: "Total (Search + Click)",
          data: chartData.total_count || [],
          backgroundColor: chartType === "bar" ? "#008000" : "rgba(0, 128, 0, 0.2)",
          borderColor: "#008000",
          borderWidth: 2,
          fill: chartType === "line"
        }
      ]
    };
  }

  defaultChartOptions(title) {
    return {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      plugins: {
        legend: {
          position: "top"
        },
        title: {
          display: true,
          text: title
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    };
  }

  updateChartSeries(chartType, chartData) {
    const chart = chartType === "user" ? this.userChart : this.institutionChart;
    if (!chart) return;

    chart.data.labels = chartData.labels || [];
    chart.data.datasets[0].data = chartData.search_count || [];
    chart.data.datasets[1].data = chartData.click_count || [];
    chart.data.datasets[2].data = chartData.total_count || [];

    chart.update({
      duration: 300,
      easing: "easeInOutQuart"
    });
  }

  updateSelectionUiState(selectedName, event) {
    if (this.hasSelectedNameTarget && selectedName) {
      this.selectedNameTarget.textContent = selectedName;
    }

    if (this.hasTableTitleTarget && selectedName) {
      const entityLabel = this.viewType === "users" ? "User Statistics: " : "Institution Statistics: ";
      this.tableTitleTarget.textContent = entityLabel + selectedName;
    }

    if (event && this.listItemTargets && this.listItemTargets.length > 0) {
      this.listItemTargets.forEach((item) => item.classList.remove("active"));
      event.currentTarget.classList.add("active");
    }
  }

  updateSelectedIdInUrl(id) {
    const url = new URL(window.location.href);
    url.searchParams.set("selected_id", id);
    window.history.replaceState({}, "", url.toString());
    
    // Update export links with new selected_id
    this.updateExportLinks(id);
  }

  // Update export link hrefs with current selected_id
  updateExportLinks(selectedId) {
    const baseUrl   = window.location.origin + window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set("selected_id", selectedId);
    urlParams.set("tab", "individual");
    
    const paramsString = urlParams.toString();
    
    const csvLink  = document.getElementById("export-csv-link");
    const xlsxLink = document.getElementById("export-xlsx-link");
    const pdfLink  = document.getElementById("export-pdf-link");
    
    if (csvLink) {
      csvLink.href = `${baseUrl}?${paramsString}&format=csv`;
    }
    if (xlsxLink) {
      xlsxLink.href = `${baseUrl}?${paramsString}&format=xlsx`;
    }
    if (pdfLink) {
      pdfLink.href = `${baseUrl}?${paramsString}&format=pdf`;
    }
  }

  firstUserId() {
    const stats = this.allUsersStatistics || {};
    const keys = Object.keys(stats);
    return keys.length > 0 ? parseInt(keys[0], 10) : null;
  }

  firstInstitutionId() {
    const stats = this.allInstitutionsStatistics || {};
    const keys = Object.keys(stats);
    return keys.length > 0 ? parseInt(keys[0], 10) : null;
  }

  reloadWithParams(params) {
    const url = new URL(window.location.href);

    url.searchParams.set("tab", "individual");
    url.searchParams.set("view_type", this.viewType);

    if (params.selected_id) {
      url.searchParams.set("selected_id", params.selected_id);
    }

    url.searchParams.set("sort_order", this.sortOrder);
    url.searchParams.set("chart_type", this.chartType);
    url.searchParams.set("start_date", this.startDate);
    url.searchParams.set("end_date", this.endDate);
    url.searchParams.set("aggregation", this.aggregation);

    window.location.href = url.toString();
  }

  // Switch view type (users/institutions)
  switchViewType(event) {
    const type = event.currentTarget.value;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "individual");
    url.searchParams.set("view_type", type);
    url.searchParams.set("chart_type", this.chartType);
    url.searchParams.delete("selected_id");
    window.location.href = url.toString();
  }

  // Sort list (asc/desc)
  sortList(event) {
    const order = event.currentTarget.dataset.sortOrder;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", "individual");
    url.searchParams.set("view_type", this.viewType);
    url.searchParams.set("sort_order", order);
    url.searchParams.set("chart_type", this.chartType);
    url.searchParams.delete("selected_id");
    window.location.href = url.toString();
  }

  // Apply date filter
  applyIndividualStatisticsFilter() {
    const startDate = document.getElementById("individual_start_date_picker").value;
    const endDate = document.getElementById("individual_end_date_picker").value;
    const aggregation = document.getElementById("individual_aggregation_select").value;

    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set("start_date", startDate);
    url.searchParams.set("end_date", endDate);
    url.searchParams.set("aggregation", aggregation);
    url.searchParams.set("tab", "individual");
    url.searchParams.set("view_type", this.viewType);
    url.searchParams.set("sort_order", this.sortOrder);
    url.searchParams.set("chart_type", this.chartType);
    if (this.selectedId) {
      url.searchParams.set("selected_id", this.selectedId);
    }
    url.searchParams.set("apply", "true");

    window.location.href = url.toString();
  }
}

